import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FireIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArchiveBoxIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  getDashboardStats,
  DashboardStats,
  DashboardVencimientoItem,
} from '../services/dashboardService';
import { useCenter } from '../contexts/CenterContext';

const PIE_COLORS = ['#EF4444', '#F59E0B', '#10B981'];

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentCenter } = useCenter();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [currentCenter?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setStats(await getDashboardStats());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const vencidosTotal = (stats?.extintoresVencidosCarga ?? 0) + (stats?.extintoresVencidosPh ?? 0);
  const okParque = Math.max(
    0,
    (stats?.activeExtintores ?? 0) -
      (stats?.extintoresVencidosCarga ?? 0) -
      (stats?.extintoresPorVencer30 ?? 0),
  );

  const pieData = [
    { name: 'Vencidos', value: vencidosTotal, color: PIE_COLORS[0] },
    { name: 'Por vencer 30d', value: stats?.extintoresPorVencer30 ?? 0, color: PIE_COLORS[1] },
    { name: 'Al día (aprox.)', value: okParque, color: PIE_COLORS[2] },
  ].filter((d) => d.value > 0);

  const remitoChartData =
    stats?.remitosPorEstado.map((r) => ({
      name: r.nombre.replace(' a chofer', '').replace(' a sistema', ''),
      cantidad: r.cantidad,
    })) ?? [];

  const kpiCards = stats
    ? [
        {
          name: 'Vencidos carga',
          value: stats.extintoresVencidosCarga,
          hint: 'Extintores activos',
          icon: ExclamationTriangleIcon,
          color: 'bg-red-500',
          onClick: () => navigate('/extintores?estado=vencidosCarga'),
        },
        {
          name: 'Vencidos PH',
          value: stats.extintoresVencidosPh,
          hint: 'Prueba hidráulica',
          icon: FireIcon,
          color: 'bg-orange-500',
          onClick: () => navigate('/extintores?estado=vencidosPh'),
        },
        {
          name: 'Por vencer (30 días)',
          value: stats.extintoresPorVencer30,
          hint: 'Carga o PH',
          icon: ClockIcon,
          color: 'bg-amber-500',
          onClick: () => navigate('/extintores?estado=porVencer'),
        },
        {
          name: 'En reserva',
          value: stats.extintoresEnReserva,
          hint: 'Stock no instalado',
          icon: ArchiveBoxIcon,
          color: 'bg-slate-500',
          onClick: () => navigate('/extintores?estado=reserva'),
        },
        {
          name: 'Tareas pendientes',
          value: stats.pendingTasks,
          hint: 'Pendiente / asignada',
          icon: ClipboardDocumentListIcon,
          color: 'bg-blue-500',
          onClick: () => navigate('/tareas'),
        },
        {
          name: 'OT en taller',
          value: stats.ordersInProcess,
          hint: 'Pendiente / proceso',
          icon: WrenchScrewdriverIcon,
          color: 'bg-violet-500',
          onClick: () => navigate('/ordenes-trabajo'),
        },
        {
          name: 'Remitos en curso',
          value: stats.remitosEnCurso,
          hint: 'Hasta producción',
          icon: TruckIcon,
          color: 'bg-teal-500',
          onClick: () => navigate('/remitos'),
        },
        {
          name: 'Puestos sin extintor',
          value: stats.puestosSinExtintor,
          hint: 'Activos sin equipo',
          icon: MapPinIcon,
          color: 'bg-rose-500',
          onClick: () => navigate('/puestos?estado=sinExtintor'),
        },
      ]
    : [];

  const formatFecha = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('es-AR');
    } catch {
      return iso;
    }
  };

  const diasLabel = (item: DashboardVencimientoItem) => {
    if (item.dias < 0) return `Vencido hace ${Math.abs(item.dias)} d`;
    if (item.dias === 0) return 'Vence hoy';
    return `En ${item.dias} d`;
  };

  /** Formato pedido: Orden (x) - Extintor (x) - Vto (carga/PH) */
  const formatProximoVencimiento = (item: DashboardVencimientoItem) => {
    const orden =
      item.orden != null && item.orden > 0 ? String(item.orden) : '—';
    const extintor = item.extintorId != null ? String(item.extintorId) : '—';
    const vto = item.tipo?.trim() || '—';
    return `Orden (${orden}) - Extintor (${extintor}) - Vto (${vto})`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard operativo</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Vencimientos, parque y pipeline del centro actual
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1">
            <BuildingOfficeIcon className="w-4 h-4" />
            {stats?.totalClients ?? '—'} clientes
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1">
            <MapPinIcon className="w-4 h-4" />
            {stats?.totalSucursales ?? '—'} sucursales
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1">
            <FireIcon className="w-4 h-4" />
            {stats?.totalExtintores ?? '—'} extintores
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1">
            <CubeIcon className="w-4 h-4" />
            {stats?.totalElementos ?? '—'} elementos
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="card p-5 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-3" />
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
            ))
          : kpiCards.map((stat) => (
              <button
                key={stat.name}
                type="button"
                onClick={stat.onClick}
                className="card p-5 text-left hover:border-primary-300 dark:hover:border-primary-500 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {stat.name}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                      {stat.value}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.hint}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color} shrink-0`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </button>
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Estado del parque (extintores)
          </h3>
          {loading ? (
            <div className="h-[280px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : pieData.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">
              Sin extintores activos en el alcance actual
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${percent ? Math.round(percent * 100) : 0}%`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Pipeline de remitos
          </h3>
          {loading ? (
            <div className="h-[280px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : remitoChartData.every((r) => r.cantidad === 0) ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">
              No hay remitos en el alcance actual
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={remitoChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="cantidad" fill="#0D9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Próximos vencimientos
            </h3>
            <button
              type="button"
              onClick={() => navigate('/extintores')}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Ver extintores
            </button>
          </div>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded" />
              ))}
            </div>
          ) : !stats?.proximosVencer?.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
              No hay vencimientos para mostrar
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {stats.proximosVencer.map((item) => (
                <li key={`${item.extintorId}-${item.tipo}-${item.fecha}`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/extintores/${item.extintorId}`)}
                    className="w-full flex items-start justify-between gap-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 px-1 rounded"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {formatProximoVencimiento(item)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {[item.clienteNombre, item.sucursalNombre].filter(Boolean).join(' · ') ||
                          'Sin ubicación'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFecha(item.fecha)}
                      </p>
                      <p
                        className={`text-xs font-medium ${
                          item.dias < 0
                            ? 'text-red-600 dark:text-red-400'
                            : item.dias <= 30
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {diasLabel(item)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Accesos rápidos
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Extintores', path: '/extintores', icon: FireIcon },
              { label: 'Puestos', path: '/puestos', icon: MapPinIcon },
              { label: 'Tareas', path: '/tareas', icon: ClipboardDocumentListIcon },
              { label: 'Remitos', path: '/remitos', icon: TruckIcon },
              {
                label: 'Órdenes de trabajo',
                path: '/ordenes-trabajo',
                icon: WrenchScrewdriverIcon,
              },
              { label: 'Clientes', path: '/clientes', icon: BuildingOfficeIcon },
            ].map((action) => (
              <button
                key={action.path}
                type="button"
                onClick={() => navigate(action.path)}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:hover:border-primary-400 dark:hover:bg-primary-900/20 transition-colors"
              >
                <action.icon className="w-5 h-5 text-primary-500 shrink-0" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
